import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import type { LessonPriceChange } from "../../api/backoffice.api";
import { MoneyInput } from "../../inputs/MoneyInput";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import type { DialogProps } from "../../misc/DialogProps";
import { Form2 } from "../../misc/Form2";
import { formatMoney } from "../../misc/Money";
import type { Money } from "../../model/Money";

export function LessonPriceDifferenceDialog(
  props: DialogProps & {
    studentName: string;
    isManualPrice: boolean;
    prices: { oldPrice: Money; oldPriceSuggestion: Money; newPriceSuggestion: Money };
    onConfirm: (choice: LessonPriceChange) => Promise<void>;
  },
) {
  const { open, onClose, studentName, isManualPrice, prices, onConfirm } = props;
  const { oldPrice, oldPriceSuggestion, newPriceSuggestion } = prices;
  const [applyDifference, setApplyDifference] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isIncrease = newPriceSuggestion.amount >= oldPrice.amount;
  const difference = formatMoney(Math.abs(newPriceSuggestion.amount - oldPrice.amount));

  async function onSubmit(data: { manualStudentGross: Money }) {
    const choice: LessonPriceChange = isManualPrice
      ? {
          manualStudentGross: {
            ...data.manualStudentGross,
            amount: Math.round(data.manualStudentGross.amount * 100) / 100,
          },
        }
      : { handlePriceDifferenceStrategy: applyDifference ? "updatePrice" : "ignore" };
    setIsSubmitting(true);
    try {
      await onConfirm(choice);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isManualPrice ? "Neuer Preis?" : isIncrease ? "Preisdifferenz nachberechnen?" : "Preisdifferenz gutschreiben?"}
      </DialogTitle>
      <DialogCloseButton onClick={onClose} disabled={isSubmitting} />
      <Form2 defaultValues={{ manualStudentGross: oldPrice }} onSubmit={onSubmit}>
        <DialogContent>
          {isManualPrice ? (
            <Stack gap={2}>
              <Typography variant="body2" color="text.secondary">
                Der Preis war zuvor {formatMoney(oldPrice)} (statt {formatMoney(oldPriceSuggestion)}). Durch deine
                Änderungen wäre der neue Preis normalerweise: {formatMoney(newPriceSuggestion)}. Bitte lege fest,
                welcher Preis tatsächlich berechnet werden soll.
              </Typography>
              <MoneyInput
                source="manualStudentGross"
                label="Neuer Preis"
                autoFocus
                isRequired
                disabled={isSubmitting}
                validate={(value: Money | null) =>
                  !value || !Number.isFinite(value.amount) || value.amount < 0
                    ? "Bitte gib einen gültigen Preis ab 0 € ein."
                    : undefined
                }
              />
            </Stack>
          ) : (
            <Stack gap={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                gap={3}
                sx={{ p: 2, bgcolor: "action.hover", borderRadius: 2 }}
              >
                <Stack gap={0.5} sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Bisheriger Preis
                  </Typography>
                  <Typography variant="h6" component="p" sx={{ fontWeight: 500 }}>
                    {formatMoney(oldPrice)}
                  </Typography>
                </Stack>
                <Stack gap={0.5} sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Neuer Preis
                  </Typography>
                  <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
                    {formatMoney(newPriceSuggestion)}
                  </Typography>
                </Stack>
              </Stack>
              <FormControl disabled={isSubmitting}>
                <RadioGroup
                  aria-label={isIncrease ? "Nachberechnung" : "Gutschrift"}
                  value={applyDifference ? "updatePrice" : "ignore"}
                  onChange={(_, value) => setApplyDifference(value === "updatePrice")}
                  sx={{
                    gap: 1,
                    "& .MuiFormControlLabel-root": {
                      m: 0,
                      p: 1.5,
                      alignItems: "flex-start",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderRadius: 2,
                      "& .MuiRadio-root": { p: 0.5, mr: 1 },
                    },
                  }}
                >
                  <FormControlLabel
                    disableTypography
                    value="updatePrice"
                    control={<Radio />}
                    sx={{
                      borderColor: applyDifference ? "primary.main" : "divider",
                      bgcolor: applyDifference ? "action.selected" : "transparent",
                    }}
                    label={
                      <Stack gap={0.5}>
                        <Typography variant="subtitle2" component="p" sx={{ fontWeight: 600 }}>
                          {isIncrease ? "Nachberechnung" : "Gutschrift"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {studentName} wird die Differenz von {difference}{" "}
                          {isIncrease ? "in Rechnung gestellt." : "gutgeschrieben."}
                        </Typography>
                      </Stack>
                    }
                  />
                  <FormControlLabel
                    disableTypography
                    value="ignore"
                    control={<Radio />}
                    sx={{
                      borderColor: !applyDifference ? "primary.main" : "divider",
                      bgcolor: !applyDifference ? "action.selected" : "transparent",
                    }}
                    label={
                      <Stack gap={0.5}>
                        <Typography variant="subtitle2" component="p" sx={{ fontWeight: 600 }}>
                          {isIncrease ? "Keine Nachberechnung" : "Keine Gutschrift"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {studentName} wird nichts {isIncrease ? "nachberechnet." : "gutgeschrieben."}
                        </Typography>
                      </Stack>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Bestätigen
          </LoadingButton>
        </DialogActions>
      </Form2>
    </Dialog>
  );
}
